import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, KanbanSquare, Bell, LifeBuoy, ArrowRight, ArrowLeft, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STEPS = [
  {
    id: "clients",
    icon: Users,
    title: "Your client roster",
    body: "Every dog you train lives on the dashboard with goals, notes, sessions and balances in one card. Tap any client to drill into their full history.",
    accent: "from-amber-200 to-amber-100",
  },
  {
    id: "pipeline",
    icon: KanbanSquare,
    title: "Lead pipeline",
    body: "Switch to the Pipeline view to drag leads through New → Contacted → Consultation → Won. Time-in-stage analytics show where leads stall.",
    accent: "from-sky-200 to-sky-100",
  },
  {
    id: "reminders",
    icon: Bell,
    title: "Automatic reminders",
    body: "Pawbook auto-emails session reminders 24h before, and notifies you when a lead changes stages. Send a manual reminder anytime from a client card.",
    accent: "from-emerald-200 to-emerald-100",
  },
  {
    id: "help",
    icon: LifeBuoy,
    title: "We're here when you need us",
    body: "Hit \"Need help?\" in the top-right anytime to email our team. Reply within one business day, even on weekends during your beta.",
    accent: "from-rose-200 to-rose-100",
  },
];

/**
 * Lightweight 4-step welcome tour for first-time invited users.
 * Mobile-friendly modal overlay — no third-party tour library.
 */
export default function WelcomeTour({ onClose }) {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  async function finish(label) {
    if (closing) return;
    setClosing(true);
    try {
      await api.post("/auth/complete-tour");
      if (refreshUser) await refreshUser();
      if (onClose) onClose(label);
    } catch {
      // Even if the network blip happens, dismiss locally so users aren't trapped.
      if (onClose) onClose(label);
    }
  }

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div
      data-testid="welcome-tour"
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        {/* Header banner */}
        <div
          className={`bg-gradient-to-br ${current.accent} px-6 pb-12 pt-6 sm:px-8`}
          data-testid={`tour-step-${current.id}`}
        >
          <div className="flex items-start justify-between">
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-semibold text-slate-700">
              Step {step + 1} of {STEPS.length}
            </span>
            <button
              type="button"
              onClick={() => finish("close")}
              data-testid="tour-close-btn"
              className="rounded-full p-1 text-slate-700 hover:bg-white/60"
              aria-label="Close tour"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-4 inline-flex rounded-2xl bg-white/90 p-3 text-slate-900 shadow-sm">
            <Icon size={26} />
          </div>
        </div>

        <div className="-mt-8 px-6 pb-6 pt-2 sm:px-8 sm:pb-7">
          <div className="rounded-2xl bg-white px-1 pt-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <h2 data-testid="tour-title" className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                  {current.title}
                </h2>
                <p data-testid="tour-body" className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {current.body}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress dots */}
          <div className="mt-6 flex items-center justify-center gap-1.5" aria-hidden>
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-slate-900" : "w-1.5 bg-slate-300"
                }`}
              />
            ))}
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-between gap-2">
            <Button
              data-testid="tour-skip-btn"
              variant="ghost"
              onClick={() => finish("skip")}
              className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            >
              Skip tour
            </Button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button
                  data-testid="tour-prev-btn"
                  variant="outline"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="rounded-full"
                >
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
              )}
              {!isLast ? (
                <Button
                  data-testid="tour-next-btn"
                  onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                  className="rounded-full bg-slate-900 hover:bg-slate-800"
                >
                  Next <ArrowRight size={14} className="ml-1" />
                </Button>
              ) : (
                <Button
                  data-testid="tour-finish-btn"
                  onClick={() => finish("finish")}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check size={14} className="mr-1" /> Finish tour
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
